import React, { useEffect, useMemo } from 'react';
import { TBaseTagContext } from '../types';
import { find, isFunction } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { TTag } from './types';
import { validate } from '@krugom/validator';
import { selectTemplate } from '../../../store/selector/selectTemplate';
import { selectApiState } from '../../../store/selector/selectApiState';
import { ApiRequest } from '../../../store/saga/api/actions';
import * as config from '../../../config';
import { templates } from '../../../store/saga/initial/constants/templates';
import { TValidationScheme } from '@krugom/validator/dist/types';

const getScheme = (tagProps: TTag): TValidationScheme => ({
  type: 'object',
  properties: {
    name: { type: 'string', required: !tagProps.id },
    id: { type: 'string', required: !tagProps.name },
    children: { type: 'custom', validator: (value: any) => !isFunction(value) },
  },
});

export const CustomTemplate: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const { id, name, children } = tagProps;
  const result = validate(tagProps, getScheme(tagProps));

  const requestKey = `get-template-${name || id}`;

  const propsSelector = useSelector(
    !result.isValid
      ? () => undefined
      : (state: RootState) =>
          selectTemplate(state, name, id) ||
          find(templates, item => item.name === name || item.id === id)
  );

  const templateProps = result.isValid ? propsSelector : undefined;

  const statusSelector = useSelector((state: RootState) => selectApiState(state, requestKey));

  const loadingStatus = result.isValid ? statusSelector?.status || 'init' : 'init';

  const dispatch = useDispatch();
  useEffect(() => {
    if (result.isValid && !templateProps && loadingStatus === 'init') {
      dispatch(
        ApiRequest({
          path: `${config.api.templatesPath}${id ? '/' + id : ''}`,
          method: 'GET',
          key: requestKey,
          query: name ? { filters: { name } } : undefined,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, loadingStatus, name, templateProps, result.isValid, id, tagProps]);

  const templateContext = useMemo(
    () => ({
      ...context,
      path: [...(context.path || []), (name || id) as string],
      template: { status: loadingStatus || 'init', children, name, id },
    }),
    [context, loadingStatus, name, id, children]
  );

  if (!result.isValid) {
    console.error(
      'Тэг: $CustomTemplate;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  if ((name && context.path?.includes(name)) || (id && context.path?.includes(id))) {
    console.error(`Template tag (name: "${name}") is child of itself!`);
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  return <UniversalChildren context={templateContext}>{templateProps?.template}</UniversalChildren>;
});

CustomTemplate.displayName = 'CustomTemplate';
